import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NewApiBaseUrl } from '../../consts/new-api-base-url'
import useHttpApiNew from '../../hooks/useHttpApiNew'
import { NOTIFICATION_PAGE_SIZE } from './notification-center.const'
import { applyNotificationRead } from './notification-read-state.util'

function buildActionLogUrl(path, params) {
  const url = new URL(`${NewApiBaseUrl}/ActionLog${path}`)
  params.forEach(([key, value]) => url.searchParams.append(key, String(value)))
  return url
}

/**
 * Paged Notification Center feed with per-tab counts.
 * @param {{ token: string|null, tab: string, search: string, filterParams: Array<[string, string]>, refreshKey: number }} options
 */
export function useNotificationFeed({ token, tab, search, filterParams, refreshKey }) {
  const { request } = useHttpApiNew()
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [hasError, setHasError] = useState(false)
  const requestIdRef = useRef(0)

  const baseParams = useMemo(
    () => (search ? [...filterParams, ['search', search]] : filterParams),
    [filterParams, search],
  )
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token])

  const fetchPage = useCallback(async offset => {
    const requestId = ++requestIdRef.current
    setIsLoading(true)
    const params = [...baseParams, ['tab', tab], ['limit', NOTIFICATION_PAGE_SIZE], ['offset', offset]]
    const response = await request(buildActionLogUrl('', params), 'GET', null, authHeaders)
    if (requestId !== requestIdRef.current) return
    setIsLoading(false)
    setHasError(Boolean(!response || response.responseAnyError))
    if (!response || response.responseAnyError) return
    const page = response.responseData ?? []
    setItems(previous => (offset === 0 ? page : [...previous, ...page]))
    setHasMore(page.length === NOTIFICATION_PAGE_SIZE)
  }, [baseParams, tab, request, authHeaders])

  const fetchSummary = useCallback(async () => {
    const response = await request(buildActionLogUrl('/summary', baseParams), 'GET', null, authHeaders)
    if (!response || response.responseAnyError) return
    setSummary(response.responseData)
  }, [baseParams, request, authHeaders])

  useEffect(() => {
    if (!token) return
    fetchPage(0)
    fetchSummary()
  }, [token, fetchPage, fetchSummary, refreshKey])

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) fetchPage(items.length)
  }, [isLoading, hasMore, fetchPage, items.length])

  const markReadLocally = useCallback(id => {
    setItems(previous => applyNotificationRead(previous, id, tab))
    setSummary(previous => (previous ? { ...previous, unread: Math.max(0, previous.unread - 1) } : previous))
  }, [tab])

  return { items, summary, isLoading, hasMore, hasError, loadMore, markReadLocally }
}
